import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useCalendarioAprovacao from 'hooks/Relatorios/Aprovacao/useCalendarioAprovacao';
import useDessemCenarios, { CenarioOpcao, cenarioParaODia } from 'hooks/Relatorios/Dessem/useDessemCenarios';
import useDessemMaxData from 'hooks/Relatorios/Dessem/useDessemMaxData';
import useDessemSecaoParams from 'hooks/Relatorios/Dessem/useDessemSecaoParams';
import {
  selectDessemDataAnalisada,
  selectDessemCenario,
  setDessemDataAnalisada,
  setDessemCenario,
} from 'redux/reducers/relatoriosDessemReducer';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { DessemCenario } from 'types/Relatorios/Dessem';

const config = getAprovacaoConfig('dessem');

export interface DessemControlPanelState {
  selecionada: Date;
  handleDataChange: (date: Date | null) => void;
  maxData: Date;
  datePickerProps: ReturnType<typeof useCalendarioAprovacao>['datePickerProps'];
  mostraLegenda: boolean;
  cenarios: CenarioOpcao[];
  carregando: boolean;
  cenarioAplicado: DessemCenario;
  handleCenarioChange: (value: DessemCenario) => void;
}

/**
 * Estado e ações do painel de controle do Informe DESSEM, compartilhado pelo painel desktop
 * (`DessemControlPanel`, no `RightSidebar`) e pelo mobile (`ConsultaCard` + `ControlPanelSheet`).
 *
 * Deve rodar em **um único** componente sempre montado por plataforma: o efeito D1 abaixo
 * (trocar o cenário aplicado quando ele não existe no dia) precisa rodar mesmo com o
 * BottomSheet fechado e não pode rodar em dobro.
 *
 * Sem botão "Atualizar" (Planejamento, 13/10/2026): `handleDataChange` e `handleCenarioChange`
 * já aplicam no slice. O dia do calendário é a Data do Informe (D); o teto (`maxData`) é o último
 * dia com estudo (`useDessemMaxData`). Com a aprovação habilitada o calendário traz as bolinhas
 * de status (admin) e só deixa o cliente selecionar dias publicados (`datePickerProps`).
 */
export default function useDessemControlPanel(): DessemControlPanelState {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectDessemDataAnalisada);
  const cenarioAplicado = useSelector(selectDessemCenario);

  const {
    datePickerProps, mostraLegenda, onDataSelecionada,
  } = useCalendarioAprovacao(config, dataAnalisada, cenarioAplicado);
  const maxData = useDessemMaxData();

  const selecionada = useMemo(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
    [dataAnalisada.ano, dataAnalisada.mes, dataAnalisada.dia],
  );

  // Mesmo dia aplicado que as seções consultam (`YYYY-MM-DD`).
  const { data_rodada: dataIso } = useDessemSecaoParams();
  const { opcoes: cenarios, carregando } = useDessemCenarios(dataIso);

  // Troca de dia: o cenário aplicado pode não existir no dia novo. Enquanto o options do dia
  // carrega não há lista confiável (o hook devolve o fallback e os radios ficam travados), então
  // o efeito espera. Depois do dispatch o aplicado passa a ser disponível, então o efeito assenta.
  useEffect(() => {
    if (carregando) return;
    const proximo = cenarioParaODia(cenarios, cenarioAplicado);
    if (proximo !== null) dispatch(setDessemCenario(proximo));
  }, [cenarios, carregando, cenarioAplicado, dispatch]);

  const handleDataChange = useCallback((date: Date | null) => {
    if (!date) return;
    onDataSelecionada(date);
    dispatch(setDessemDataAnalisada({
      ano: date.getFullYear(),
      mes: date.getMonth() + 1,
      dia: date.getDate(),
    }));
  }, [dispatch, onDataSelecionada]);

  const handleCenarioChange = useCallback((value: DessemCenario) => {
    dispatch(setDessemCenario(value));
  }, [dispatch]);

  return {
    selecionada,
    handleDataChange,
    maxData,
    datePickerProps,
    mostraLegenda,
    cenarios,
    carregando,
    cenarioAplicado,
    handleCenarioChange,
  };
}
